import { forwardRef, PropsWithoutRef, Ref, ReactNode, ReactElement, ComponentType } from 'react';
import styled, { css } from 'styled-components';
import { hideVisually } from 'polished';

import type { BaseProps, ForwardProps, NoChildrenProp, PropsWithDefaults } from '../../types';
import { defaultThemeProp } from '../../theme';
import { calculateFontSize, FontSize } from '../../styles';
import Progress from '../Progress';
import Text from '../Text';
import EmptyState from '../EmptyState';
import Card, { CardContent } from '../Card';

export interface DefaultRowData {
  id: string | number;
}

export interface ColumnProps<RowData extends DefaultRowData = DefaultRowData> {
  /** Render cell content for a column with a component or by passing a property key on the row object to serve as an accessor. */
  renderer: ComponentType<RowData> | keyof RowData;
  /** The visual label for the column. */
  label: string;
  /**
   * The text alignment of the column header and the row contents for the column.
   * @default "left"
   */
  align?: 'left' | 'center' | 'right';
  /**
   * If true, this column will not wrap text on overflow.
   * @default false
   */
  noWrap?: boolean;
}

export interface TableProps<RowData extends DefaultRowData = DefaultRowData>
  extends BaseProps,
    NoChildrenProp {
  /** The column headers that will structure the Table. */
  columns: ColumnProps<RowData>[];
  /**
   * The content of the Table, represented as an array of objects with key value pairs.
   * @default []
   */
  data?: RowData[];
  /**
   * Should the data rows highlight when the mouse hovers over them.
   * @default false
   */
  hoverHighlight?: boolean;
  /**
   * Indicates if the data to populate the Table is still loading.
   * @default false
   */
  loading?: boolean;
  /** A message to be displayed when Table data is loading. */
  loadingMessage?: string;
  /** The title that will render above the Table if provided. */
  title?: ReactNode;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLTableElement>;
}

type TablePropsWithDefaults = PropsWithDefaults<TableProps, 'hoverHighlight' | 'loading'>;

const StyledTable = styled.table<Pick<TablePropsWithDefaults, 'hoverHighlight'>>(props => {
  const {
    hoverHighlight,
    theme: {
      base: {
        palette: { 'secondary-background': hoverBg },
        spacing
      },
      components: { table }
    }
  } = props;

  const verticalSpacingHeader = `calc(${table.header['vertical-spacing']} * ${spacing})`;
  const verticalSpacingBody = `calc(${table.body['vertical-spacing']} * ${spacing})`;
  const horizontalSpacing = `calc(max(${table.header['horizontal-spacing']}, ${table.body['horizontal-spacing']}) * ${spacing})`;

  const borderHeader = `${table.header['border-width']} solid ${table.header['border-color']}`;
  const borderBody = `${table.body['border-width']} solid ${table.body['border-color']}`;

  return css`
    border-collapse: collapse;
    width: 100%;
    background-color: transparent;

    thead {
      tr th {
        padding: ${`${table.spacing['vertical-inner'] ? verticalSpacingHeader : '0'} ${
          table.spacing['horizontal-inner'] ? horizontalSpacing : '0'
        }`};
        border-bottom: ${table.border['horizontal-inner'] ? borderHeader : 'none'};
        border-inline-end: ${table.border['vertical-inner'] ? borderHeader : 'none'};
      }

      tr:first-of-type th {
        padding-top: ${table.spacing['vertical-outer'] ? verticalSpacingHeader : '0'};
        border-top: ${table.border['horizontal-outer'] ? borderHeader : 'none'};
      }

      tr th:first-of-type {
        padding-inline-start: ${table.spacing['horizontal-outer'] ? horizontalSpacing : '0'};
        border-inline-start: ${table.border['vertical-outer'] ? borderHeader : 'none'};
      }

      tr th:last-of-type {
        padding-inline-end: ${table.spacing['horizontal-outer'] ? horizontalSpacing : '0'};
        border-inline-end: ${table.border['vertical-outer'] ? borderHeader : 'none'};
      }
    }

    tbody {
      tr td {
        padding: ${`${table.spacing['vertical-inner'] ? verticalSpacingBody : '0'} ${
          table.spacing['horizontal-inner'] ? horizontalSpacing : '0'
        }`};
        border-bottom: ${table.border['horizontal-inner'] ? borderBody : 'none'};
        border-inline-end: ${table.border['vertical-inner'] ? borderBody : 'none'};
      }

      tr:last-of-type td {
        padding-bottom: ${table.spacing['vertical-outer'] ? verticalSpacingBody : '0'};
        border-bottom: ${table.border['horizontal-outer'] ? borderBody : 'none'};
      }

      tr td:first-of-type {
        padding-inline-start: ${table.spacing['horizontal-outer'] ? horizontalSpacing : '0'};
        border-inline-start: ${table.border['vertical-outer'] ? borderBody : 'none'};
      }

      tr td:last-of-type {
        padding-inline-end: ${table.spacing['horizontal-outer'] ? horizontalSpacing : '0'};
        border-inline-end: ${table.border['vertical-outer'] ? borderBody : 'none'};
      }
    }

    ${hoverHighlight &&
    css`
      tbody tr:hover td,
      tbody tr:focus-within td {
        background-color: ${hoverBg};
      }
    `}
  `;
});

StyledTable.defaultProps = defaultThemeProp;

const StyledCell = styled.td<Pick<ColumnProps, 'align' | 'noWrap'>>(
  ({ align, noWrap = false }) => css`
    text-align: ${align || 'left'};
    white-space: ${noWrap ? 'nowrap' : null};
  `
);

StyledCell.defaultProps = defaultThemeProp;

const StyledHeaderCell = styled.th(
  ({
    theme: {
      base: { 'font-size': fontSize, 'font-scale': fontScale },
      components: {
        table: { header }
      }
    }
  }) => {
    const fontSizes = calculateFontSize(fontSize, fontScale);

    return css`
      font-size: ${fontSizes[header['font-size'] as FontSize]};
      font-weight: ${header['font-weight']};
      color: ${header['foreground-color']};
      background-color: ${header['background-color']};
    `;
  }
);

StyledHeaderCell.defaultProps = defaultThemeProp;

const StyledBodyCell = styled.td(
  ({
    theme: {
      components: {
        table: { body }
      }
    }
  }) => css`
    color: ${body['foreground-color']};
    background-color: ${body['background-color']};
  `
);

StyledBodyCell.defaultProps = defaultThemeProp;

const StyledEmptyCell = styled.td`
  padding: calc(2 * ${props => props.theme.base.spacing});
`;

StyledEmptyCell.defaultProps = defaultThemeProp;

const StyledLoadingCell = styled.td`
  position: relative;
  height: 6rem;
`;

const StyledTableWrapper = styled.div`
  /* Setting it on a table element requires 'display: block' which breaks a11y. */
  overflow-x: auto;
`;

StyledTableWrapper.defaultProps = defaultThemeProp;

const StyledTableCaption = styled.caption`
  ${hideVisually}
`;

StyledTableCaption.defaultProps = defaultThemeProp;

const Table = forwardRef((props: PropsWithoutRef<TableProps>, ref: Ref<HTMLTableElement>) => {
  const {
    columns,
    data = [],
    hoverHighlight = false,
    loading = false,
    loadingMessage = '',
    title,
    ...restProps
  } = props;

  return (
    <Card>
      <CardContent container={{ rowGap: 1 }}>
        {title && (
          <Text variant='h2' as='span' aria-hidden>
            {title}
          </Text>
        )}
        <StyledTableWrapper>
          <StyledTable ref={ref} hoverHighlight={hoverHighlight} {...restProps}>
            {title && <StyledTableCaption>{title}</StyledTableCaption>}
            <thead>
              <tr>
                {columns.map(col => (
                  <StyledCell as={StyledHeaderCell} {...col} scope='col' key={col.label}>
                    {col.label}
                  </StyledCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <StyledLoadingCell colSpan={columns.length}>
                    <Progress placement='local' message={loadingMessage} />
                  </StyledLoadingCell>
                </tr>
              )}
              {data.length > 0 &&
                !loading &&
                data.map(row => (
                  <tr key={row.id}>
                    {columns.map(({ renderer: Renderer, ...col }) => (
                      <StyledCell as={StyledBodyCell} {...col} key={`${col.label}-${row.id}`}>
                        {typeof Renderer === 'function' ? <Renderer {...row} /> : row[Renderer]}
                      </StyledCell>
                    ))}
                  </tr>
                ))}
              {data.length === 0 && !loading && (
                <tr>
                  <StyledEmptyCell colSpan={columns.length}>
                    <EmptyState />
                  </StyledEmptyCell>
                </tr>
              )}
            </tbody>
          </StyledTable>
        </StyledTableWrapper>
      </CardContent>
    </Card>
  );
}) as <RowData extends DefaultRowData = DefaultRowData>(
  props: TableProps<RowData> & ForwardProps
) => ReactElement | null;

export default Table;
